import { SalesTaxFiling } from '@commenda/api';
import { Badge, DataTable, Icons } from '@commenda/kakashi';
import { ServiceRequestModel } from '@commenda/utils';
import { NonTimezoneSensitiveDate } from '@commenda/utils/non-time-zone-sensitive-date';
import { useRouter } from 'next/router';
import {
  FilingReviewStatusColorMap,
  FilingReviewStatusNameMap,
} from '../../../../molecules';
import { parseSalesTaxApiFilingDataToStandardFilingData } from '../../../../sales-tax/filing/dashboard/useSalesTaxFilingList';

type PrepaymentLogTableProps = {
  prepayments: SalesTaxFiling[];
  corporationId: number;
};

const PrepaymentLogTable = ({
  prepayments,
  corporationId,
}: PrepaymentLogTableProps) => {
  const router = useRouter();
  const filings = prepayments.map((prepayment) =>
    parseSalesTaxApiFilingDataToStandardFilingData(prepayment)
  );

  const handleCopy = (value: string | number) => {
    navigator.clipboard.writeText(value.toString());
  };

  const onFilingItemClick = (filingId: string) => {
    if (!filingId) return;
    router.push(ServiceRequestModel.getAgentFilingLink(filingId, corporationId));
  };

  const CopyableCell = ({ value }: { value: string | number }) => (
    <div className="flex items-center group relative w-full">
      <span>
        {typeof value === 'number' ? `$${value.toLocaleString()}` : value}
      </span>
      <button
        type="button"
        className="opacity-0 group-hover:opacity-100 transition-opacity absolute right-1 p-1 bg-white rounded-sm shadow-sm hover:shadow-md"
        onClick={() => handleCopy(value)}
      >
        <Icons.Copy01 className="text-text-tertiary scale-75" />
      </button>
    </div>
  );

  const columns = [
    {
      id: 'filingName',
      accessorKey: 'filingName',
      header: 'Filing Name',
      cell: ({ row }) => (
        <span
          className="txt-medium text-text-primary cursor-pointer underline text-text-primary-hover"
          onClick={() => onFilingItemClick(row.original.filing_id)}
        >
          {row.original.filingName}
        </span>
      ),
    },
    {
      id: 'dueDate',
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }) => {
        const formattedDate = NonTimezoneSensitiveDate.fromISO8601String(
          row.original.dueDate
        ).format('MMM DD');

        return (
          <Badge variant="gray" size="sm">
            {formattedDate}
          </Badge>
        );
      },
    },
    {
      id: 'filingStatus',
      accessorKey: 'filingStatus',
      header: 'Status',
      cell: ({ row }) => (
        <Badge
          variant={FilingReviewStatusColorMap[row.original.filingStatus]}
          size="sm"
        >
          {FilingReviewStatusNameMap[row.original.filingStatus]}
        </Badge>
      ),
    },
    {
      id: 'tax_liability',
      accessorKey: 'tax_liability',
      header: 'Tax Liability',
      cell: ({ row }) => {
        const taxLiability = row?.original?.metadata?.tax_liability;
        return <CopyableCell value={taxLiability || 0} />;
      },
    },
    {
      id: 'tax_paid',
      accessorKey: 'tax_paid',
      header: 'Tax Paid',
      cell: ({ row }) => {
        const taxPaid = row?.original?.metadata?.tax_paid;
        return <CopyableCell value={taxPaid || 0} />;
      },
    },
    {
      id: 'prepayment',
      accessorKey: 'prepayment',
      header: 'Prepayment',
      cell: ({ row }) => {
        const prepayment = row?.original?.metadata?.prepayment;
        return <CopyableCell value={prepayment || 0} />;
      },
    },
  ];

  if (prepayments.length === 0) {
    return (
      <div className="text-sm text-text-tertiary border border-border-primary rounded-xl p-4">
        No prepayment records available for this filing.
      </div>
    );
  }

  return (
    <DataTable
      columnGroups={[
        {
          id: 'filingDetails',
          columns,
        },
      ]}
      data={filings}
      tableViewConfig={{
        isEnabled: false,
        defaultView: 'expanded',
      }}
      tableRowClassName="cursor-default hover:bg-transparent"
    />
  );
};

export default PrepaymentLogTable;
